import { useCallback, useMemo, useState } from 'react';
import { isHostedByTeamHub } from '../services/hostAuthService';
import { mobileAppRedirectService } from '../services/mobileAppRedirectService';
import { detectMobilePlatform, type MobilePlatform } from '../utils/mobilePlatform';

interface MobileAppRedirect {
  /** `null` when the meeting should be joined without asking. */
  promptPlatform: MobilePlatform | null;
  joinInBrowser: () => void;
}

export function useMobileAppRedirect(meetingId: string | null): MobileAppRedirect {
  // Hosted as an MF remote the user is already inside TeamHub, so offering to
  // open it would be nonsense. Resolved once: the mode is settled before mount
  // and the user agent cannot change.
  const platform = useMemo(() => (isHostedByTeamHub() ? null : detectMobilePlatform()), []);

  // Redundant with the persisted flag on the happy path, but it is what keeps
  // the button working when `sessionStorage` is unavailable (Safari private
  // mode) and the persisted write silently no-ops.
  const [browserChoiceFor, setBrowserChoiceFor] = useState<string | null>(null);

  const joinInBrowser = useCallback(() => {
    if (!meetingId) {
      return;
    }
    mobileAppRedirectService.rememberBrowserChoice(meetingId);
    setBrowserChoiceFor(meetingId);
  }, [meetingId]);

  const shouldPrompt =
    meetingId !== null &&
    browserChoiceFor !== meetingId &&
    !mobileAppRedirectService.hasChosenBrowser(meetingId);

  return {
    promptPlatform: shouldPrompt ? platform : null,
    joinInBrowser,
  };
}
